import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, TrendingUp, Users, Building2, ExternalLink,
  ArrowRight, CheckCircle2, Globe, Camera, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const caseStudies = [
  {
    title: 'All Things Nolan',
    category: 'Entrepreneur / Artist / Global Leader',
    challenge: 'A multitalented global figure needed a website that could speak to varied audiences with seamless congruency.',
    solution: 'Created a Virtual HQ with ease of use, functional navigation, brilliant graphics, and messaging that engages all different audiences seamlessly.',
    result: 'Unified brand presence across multiple ventures and audiences.',
    gradient: 'from-purple-500 to-indigo-500',
    url: 'https://allthingsnolan.com/'
  },
  {
    title: 'Affluent Investments ATL',
    category: 'Real Estate / Staging',
    challenge: 'Digital challenges, tech support, and security concerns for a growing Atlanta real estate company.',
    solution: 'Exceeded expectations with fearless functionality, focused execution, and fresh design. Ran reconnaissance and SSL scans to ensure site security.',
    result: 'Secure, scalable digital platform supporting business growth.',
    gradient: 'from-blue-500 to-cyan-500',
    url: 'https://www.affluentinvestmentsatl.com/'
  },
  {
    title: 'Culver Consulting Firm',
    category: 'Income Tax & Accounting',
    challenge: 'Needed more than a website; required a complete business solution touching all aspects of their ecosystem.',
    solution: 'Utilized sales funnel techniques, automated processes, and CRM integration. Implemented comprehensive digital strategy.',
    result: 'Client bookings increased by 200% after implementation.',
    gradient: 'from-emerald-500 to-teal-500',
    url: 'https://www.culverconsultingfirm.com/'
  },
  {
    title: 'Gloria Joy Sherrod',
    category: 'Author / Counselor / Coach',
    challenge: 'Emerging author and entrepreneur needed to establish expert status in the mental health space.',
    solution: 'Provided DIY Platform with drag-and-drop tools plus Brand Reputation Management (BRM) to build social proof.',
    result: 'Established as recognized expert in mental health space with scaled online presence.',
    gradient: 'from-rose-500 to-pink-500',
    url: 'https://gloriajoysherrod.com/'
  },
  {
    title: 'The Digital Pulpit',
    category: 'Pastor / Author / Coach',
    challenge: 'Needed digital companion for new book release during COVID pandemic shift from physical to virtual.',
    solution: 'Created supporting platform with coaching program and SaaS for Brand Reputation Management.',
    result: 'Reviews and ratings increased by over 300%, established as expert in physical-to-virtual church transition.',
    gradient: 'from-amber-500 to-orange-500',
    url: 'https://www.thedigitalpulpit.org/'
  },
  {
    title: 'Destiny Navigators',
    category: 'Pastor / Author',
    challenge: 'Wanted to preserve unique brand look while modernizing functionality without generic minimalist design.',
    solution: 'Maintained established look while resolving hosting and security challenges. Achieved A+ security rating.',
    result: 'Best of both worlds: modernized functionality with preserved brand identity.',
    gradient: 'from-violet-500 to-purple-500',
    url: 'https://www.destinynavigators.org/'
  }
];

const industries = [
  { name: 'Accounting', icon: Building2 },
  { name: 'Authors', icon: Briefcase },
  { name: 'Beauty & Barbershop', icon: Users },
  { name: 'Celebrities', icon: Camera },
  { name: 'Churches', icon: Heart },
  { name: 'Consulting', icon: TrendingUp },
  { name: 'Education', icon: Briefcase },
  { name: 'Entertainment', icon: Camera },
  { name: 'Legal', icon: Building2 },
  { name: 'Medical', icon: Heart },
  { name: 'Non-Profits', icon: Heart },
  { name: 'Real Estate', icon: Building2 },
];

const caseStudiesSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Case Studies & Portfolio - Fearless IT",
  "description": "Explore Fearless IT's case studies showcasing custom web design & SEO solutions that drive results for diverse clients.",
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const CaseStudiesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="case-studies-page">
      <SEO 
        title="Case Studies & Portfolio - Our Client Success Stories"
        description="Explore Fearless IT's case studies showcasing custom web design, SEO, and digital solutions that drive real results. See how we've helped businesses across diverse industries grow and succeed."
        keywords="Fearless IT case studies, web design portfolio, client success stories, Las Vegas web development results, digital transformation examples, business website case studies"
        url="/company/case-studies"
        structuredData={caseStudiesSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full mb-6">
            <Briefcase className="h-4 w-4 text-cyan-600" />
            <span className="text-sm font-medium text-cyan-700">Our Work</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="case-studies-title">
            Case Studies & Portfolio
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #0891B2, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Real Results for Real Businesses
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            See how we've helped businesses across diverse industries transform their digital presence and achieve measurable growth.
          </p>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-sm font-semibold text-slate-gray uppercase tracking-wider">
              Industries We've Served
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((industry, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full text-sm text-slate-gray">
                <industry.icon className="h-4 w-4" />
                {industry.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Featured Case Studies
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Detailed looks at how we've solved real challenges for our clients.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${study.gradient}`}></div>
                <div className="p-6 sm:p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-xs font-semibold text-cyan-600 uppercase tracking-wider mb-1">
                        {study.category}
                      </div>
                      <h3 className="text-xl font-bold text-carbon-black">{study.title}</h3>
                    </div>
                    <a 
                      href={study.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 text-slate-gray" />
                    </a>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs font-semibold text-slate-gray uppercase tracking-wider mb-1">Challenge</div>
                      <p className="text-sm text-slate-gray">{study.challenge}</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-gray uppercase tracking-wider mb-1">Solution</div>
                      <p className="text-sm text-slate-gray">{study.solution}</p>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-carbon-black">{study.result}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl sm:text-5xl font-bold text-cyan-600 mb-2">30+</div>
              <div className="text-slate-gray">Clients Served</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold text-cyan-600 mb-2">200%</div>
              <div className="text-slate-gray">Avg. Booking Increase</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl font-bold text-cyan-600 mb-2">A+</div>
              <div className="text-slate-gray">Security Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Client List CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Globe className="h-12 w-12 text-cyan-500 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            See Our Full Client List
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Explore our complete portfolio of 30+ clients across diverse industries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://www.fearlessit.com/client-list"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                View All Clients
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50 transition-all w-full sm:w-auto">
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default CaseStudiesPage;
